/**
 * =====================================================================================
 * IMPORTANT: PLATFORM-SPECIFIC CLIPBOARD HANDLING - DO NOT DELETE
 * =====================================================================================
 *
 * This component implements global keyboard shortcuts (Cmd/Ctrl + C, V, Z)
 * to replicate native-like behavior in the Neutralino desktop environment.
 *
 * Why is this necessary?
 * ----------------------
 * The Neutralino webview does not automatically bind OS-level shortcuts like
 * copy and paste to the browser's default actions. We must manually capture
 * these keyboard events and trigger the corresponding clipboard operation.
 *
 * How it works:
 * -------------
 * 1. Environment Check: It uses the `useIsDesktop` hook to determine if the
 *    app is running in Neutralino or a standard web browser.
 *
 * 2. Conditional API Usage:
 *    - In DESKTOP mode, it uses the `Neutralino.clipboard` API
 *      (`clipboard.readText()` / `clipboard.writeText()`) which is the
 *      official way to interact with the system clipboard in Neutralino.
 *    - In WEB mode, it falls back to the standard `navigator.clipboard` API.
 *
 * 3. Selective Pasting: The paste handler (`Cmd/Ctrl+V`) is designed to be
 *    "global" but avoids interfering with components that have their own
 *    paste logic (like the authentication code input). It checks if the
 *    active element is an <input>, <textarea>, or has a `data-custom-paste`
 *    attribute. If so, it allows the browser's/element's default paste
 *    behavior to proceed. For all other elements, it programmatically
 *    inserts the clipboard text.
 *
 * This ensures a consistent copy-paste experience across both web and
 * desktop platforms without introducing conflicting handlers.
 *
 */
import { clipboard } from "@neutralinojs/lib";
import { useEffect } from "react";
import { useIsDesktop } from "../hooks/useIsDesktop";

const NeutralinoShortcuts = () => {
  const isDesktop = useIsDesktop();

  useEffect(() => {
    const handleKeyPress = async (event: KeyboardEvent) => {
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      switch (event.key) {
        case "z":
          if (isCtrlOrCmd) {
            event.preventDefault();
            document.execCommand("undo");
          }
          break;
        case "c":
          if (isCtrlOrCmd) {
            try {
              const selection = window.getSelection()?.toString() ?? "";
              if (isDesktop) {
                await clipboard.writeText(selection);
              } else {
                await navigator.clipboard.writeText(selection);
              }
            } catch (err) {
              console.error("Failed to copy: ", err);
            }
          }
          break;
        case "v":
          if (isCtrlOrCmd) {
            console.log("Global paste handler triggered");

            // Check if the focused element is an input or textarea, or has custom paste handling
            const activeElement = document.activeElement as HTMLElement;
            const isInputElement =
              activeElement?.tagName === "INPUT" ||
              activeElement?.tagName === "TEXTAREA" ||
              activeElement?.hasAttribute("data-custom-paste") ||
              activeElement
                ?.getAttribute("data-testid")
                ?.includes("code-input") ||
              activeElement?.closest("[data-custom-paste]");

            if (isInputElement) {
              console.log(
                "Skipping global paste - element is input/textarea or has custom paste handler",
              );
              // Don't preventDefault - let browser handle paste normally for input elements
              return;
            }

            // Only prevent default and handle paste for non-input elements
            event.preventDefault();
            console.log("Executing global paste handler");
            try {
              const text = isDesktop
                ? await clipboard.readText()
                : await navigator.clipboard.readText();
              document.execCommand("insertText", false, text);
            } catch (err) {
              console.error("Failed to paste: ", err);
            }
          }
          break;
        case "q":
          if (isCtrlOrCmd) {
            // Make sure Neutralino API is available
            if (window.Neutralino?.app) {
              window.Neutralino.app.exit();
            } else {
              console.warn("Neutralino API not available");
            }
          }
          break;
        // Add more shortcuts as needed
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    Neutralino.events.on("windowClose", () => {
      Neutralino.app.exit();
    });

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("keydown", handleKeyPress);

      Neutralino.events.off("windowClose");
    };
  }, [isDesktop]); // Empty dependency array means this effect runs once on mount

  return null; // This component doesn't render anything
};

export default NeutralinoShortcuts;
